<template>
  <div class="select_device no-drag">
    <div class="selector_top_actions" v-if="isShowUpgrade == false">
      <button class="btn request button ghost appearance_action" @click="openSoftwareTheme">
        <span class="action_icon" aria-hidden="true">
          <SvgAsset :content="softwareThemeSvg" />
        </span>
        <span>{{ $t('software_theme') }}</span>
      </button>
      <button class="btn request button ghost appearance_action" v-if="isCurrentPageKeyboard" @click="openKeyboardAppearance">
        <span class="action_icon" aria-hidden="true">
          <SvgAsset :content="keyboardAppearanceSvg" />
        </span>
        <span>{{ $t('virtual_device_style') }}</span>
      </button>

      <div class="selector_window_controls" v-if="isElectron()">
        <button class="window-btn" :title="$t('minimize') || 'Minimize'" @click="min">
          <SvgAsset :content="windowMinimizeSvg" />
        </button>
        <button class="window-btn" :title="windowControlTitle" @click="max">
          <SvgAsset v-if="!isWindowMaximized" :content="windowMaximizeSvg" />
          <SvgAsset v-else :content="windowRestoreSvg" />
        </button>
        <button class="window-btn close-btn" :title="$t('close') || 'Close'" @click="out">
          <SvgAsset :content="windowCloseSvg" />
        </button>
      </div>
    </div>

    <template v-if="isShowUpgrade == false">
      <authorize v-if="DeviceManager.instance.state.webDeviceList.concat(DeviceManager.instance.state.deviceList).length == 0" />
      <template v-else>
        <div class="select_device_scroll" ref="deviceScroll" @wheel.passive="onWheel">
          <!-- hid 设备 -->
          <div
            class="select_device_container"
            :class="{ device_select: DeviceManager.instance.state.currentDevice.ID == item.ID }"
            v-for="(item, index) in DeviceManager.instance.state.webDeviceList"
            :key="index"
            @click="selectDevice(item.ID, DeviceProtocol.hid_web)"
          >
            <div class="card_inner">
              <div class="name">
                {{ $t(getDeviceName(item.ID)) }}
                <div class="connect_mode" v-if="item.ConnectMode == DeviceConnectMode.Wireless24G">
                  <img class="m_24g" src="@/assets/img/m_24g.svg" />
                </div>
                <div class="connect_mode" v-if="item.ConnectMode == DeviceConnectMode.Wireless24G8K">
                  <img class="m_24g8k" src="@/assets/img/m_24g8k.svg" />
                </div>
              </div>
              <img class="device_img" :src="getDeviceIcon(item.ID)" />
            </div>
          </div>

          <div
            class="select_device_container"
            :class="{ device_select: DeviceManager.instance.state.currentDevice.ID == item.ID }"
            v-for="(item, index) in DeviceManager.instance.state.deviceList"
            :key="index"
            @click="selectDevice(item.ID, DeviceProtocol.dll_exe)"
          >
            <div class="card_inner">
              <!-- 威盛设备 -->
              <template v-if="EEvisionDeviceList.get(item.ID)">
                <div class="name">{{ EEvisionDeviceList.get(item.ID) }}</div>
                <img class="device_img" :src="getDeviceListIcon(item.ID)" />
              </template>
              <!-- exe 设备 -->
              <template v-else-if="getDevice(item.ID)">
                <div class="name">
                  {{ $t(getDeviceName(item.ID)) }}
                  <div class="connect_mode" v-if="item.ConnectMode == DeviceConnectMode.Wireless24G">
                    <img class="m_24g" src="@/assets/img/m_24g.svg" />
                  </div>
                  <div class="connect_mode" v-if="item.ConnectMode == DeviceConnectMode.Wireless24G8K">
                    <img class="m_24g8k" src="@/assets/img/m_24g8k.svg" />
                  </div>
                </div>
                <img class="device_img" :src="getDeviceIcon(item.ID)" />
              </template>
            </div>
          </div>
        </div>

        <div class="page_container">
          <div class="page" @click="scrollToInItem(index)" :class="{ action: currentPage == index }" v-for="(_, index) in deviceList" :key="index"></div>
        </div>
        <div class="btn_container">
          <div class="btn request button ghost" @click="requestHIDAccess" v-if="isElectron() == false">{{ $t('continue_authorizing_devices') }}</div>
          <div class="btn request button ghost" v-if="isFE87V2Old24G()" @click="toFE87V224GUpdate">固件升级</div>
        </div>

        <div class="device_notice">
          <div v-if="isSupportBluetooth">
            {{ $t('not_available_in_Bluetooth_mode') }}
          </div>
        </div>
      </template>
    </template>
    <template v-else>
      <upgrade />
    </template>
    <root_upgrade v-if="brand == Brand.irok || brand == Brand.iyx" />
  </div>
</template>
<script setup lang="ts">
import { DevicePid, EEvisionDeviceList } from '@/define/define'
import { getDevice } from '@/profiles/selector'
import { DeviceManager, DeviceProtocol } from '@/service/device'
import { computed, onMounted, onUnmounted, Ref, ref, watch } from 'vue'
import { DialogType, enableDialog } from '../cdialog/dialog'
import i18n from '@/languages/i18n'
import { DriverService } from '@/service/driver/driver'
import authorize from '../authorize/authorize.vue'
import upgrade, { isShowUpgrade } from '../upgrade/upgrade.vue'

import { DeviceConnectMode, DeviceType } from '@libs/types/enum'
import { DriverSocketMsgType } from '@libs/types/message'
import root_upgrade from '../upgrade/root_upgrade.vue'
import { isElectron } from '@libs/utils/envUtils'
import SvgAsset from '../common/SvgAsset.vue'
import { Brand, getBrand } from '@/brand/brand'
import { supportBluetooth } from '@/define/device_vendor'
import { openExternalLink } from '@/tool/utility'
import { clearKeyboardAppearancePreviewPid, openKeyboardAppearancePanel, setKeyboardAppearancePreviewPid } from '@/service/keyboard_appearance'
import { openSoftwareThemePanel } from '@/service/software_theme'
import softwareThemeSvg from '@/assets/inline-svg/software-theme.svg?raw'
import windowCloseSvg from '@/assets/inline-svg/window-close.svg?raw'
import keyboardAppearanceSvg from '@/assets/inline-svg/keyboard-appearance.svg?raw'
import windowMaximizeSvg from '@/assets/inline-svg/window-maximize.svg?raw'
import windowMinimizeSvg from '@/assets/inline-svg/window-minimize.svg?raw'
import windowRestoreSvg from '@/assets/inline-svg/window-restore.svg?raw'

const { t } = i18n.global
const deviceScroll = ref({} as HTMLElement)
const deviceList = computed(() => DeviceManager.instance.state.webDeviceList.concat(DeviceManager.instance.state.deviceList))

const brand: Ref<Brand | null> = ref(null)
const isWindowMaximized = ref(false)

const syncWindowMaximizedState = async () => {
  if (!isElectron()) {
    isWindowMaximized.value = false
    return
  }
  isWindowMaximized.value = Boolean(await electronAPI.wnd('isMaximized'))
}

const handleWindowResize = () => {
  void syncWindowMaximizedState()
}

const windowControlTitle = computed(() => (isWindowMaximized.value ? t('restore') || 'Restore' : t('maximize') || 'Maximize'))

// 通过deviceID获取设备信息
const getDeviceName = (deviceID: DevicePid): string => {
  return getDevice(deviceID)?.name || ''
}
const getDeviceIcon = (deviceID: DevicePid): string => {
  return getDevice(deviceID)?.icon || ''
}
onMounted(() => {
  void syncWindowMaximizedState()
  if (isElectron()) {
    window.addEventListener('resize', handleWindowResize)
  }
  getBrand().then((value) => {
    brand.value = value
  })
})

onUnmounted(() => {
  if (isElectron()) {
    window.removeEventListener('resize', handleWindowResize)
  }
})

const selectDevice = (pid: DevicePid, mode: DeviceProtocol) => {
  if (isFE87V2Old24G()) {
    enableDialog(t('fe87v2_24g_update_notice'), DialogType.confirm).then((value: boolean) => {
      if (value) {
        toFE87V224GUpdate()
      }
    })
    return
  }
  if (EEvisionDeviceList.get(pid) != undefined) {
    enableDialog(t('open_eevision_notice'), DialogType.confirm).then((value: boolean) => {
      if (value) {
        DriverService.instance.lanuchNcus()
      }
    })
  } else {
    DeviceManager.instance.selectCurrentDevice(pid, mode)
  }
}
const requestHIDAccess = () => DriverService.instance.askType(DriverSocketMsgType.MSRequestDevice)
let currentPage = ref(0)
const onWheel = (e: WheelEvent) => {
  if (e.deltaY < 0) {
    const page = currentPage.value - 1
    if (page < 0) {
      return
    }
    scrollToInItem(page)
  } else {
    const page = currentPage.value + 1
    if (page >= deviceList.value.length) {
      return
    }
    scrollToInItem(page)
  }
}

const scrollToInItem = (page: number) => {
  currentPage.value = page
  // 700px (card) + 80px (gap) = 780px offset
  deviceScroll.value.scrollTo({ left: 780 * page, behavior: 'smooth' })
}
const getDeviceListIcon = (item: number) => {
  return new URL(`../../assets/img/device/${EEvisionDeviceList.get(item) || 'AL87'}.png`, import.meta.url).href
}

const isSupportBluetooth = computed(() => {
  return supportBluetooth.includes(deviceList.value[currentPage.value].ID)
})

const isCurrentPageKeyboard = computed(() => {
  const currentDevice = deviceList.value[currentPage.value]
  if (!currentDevice) {
    return false
  }
  return getDevice(currentDevice.ID)?.deviceType === DeviceType.keyboard
})

const syncKeyboardAppearancePreviewPid = () => {
  const currentDevice = deviceList.value[currentPage.value]
  if (!currentDevice) {
    clearKeyboardAppearancePreviewPid()
    return
  }

  const previewDevice = getDevice(currentDevice.ID)
  if (previewDevice?.deviceType === DeviceType.keyboard) {
    setKeyboardAppearancePreviewPid(currentDevice.ID)
    return
  }

  clearKeyboardAppearancePreviewPid()
}

const isFE87V2Old24G = () => {
  const deviceID = deviceList.value[currentPage.value].ID
  const productName = deviceList.value[currentPage.value].ProductName
  return deviceID == DevicePid.IrokFe87V2_24g && productName && productName.indexOf('Fe87 V2') > -1
}

const toFE87V224GUpdate = () => {
  openExternalLink('https://www.irok.cn/news/64')
}

const openKeyboardAppearance = () => {
  // 预览 pid 由下面两个 watcher（currentPage / deviceList）始终与当前卡片保持同步，打开面板时无需重复同步。
  openKeyboardAppearancePanel()
}

const openSoftwareTheme = () => {
  // 键盘预览 pid 在 closeKeyboardAppearancePanel 内部已清空（registry 互斥触发），这里不需重复。
  openSoftwareThemePanel()
}

watch(currentPage, () => {
  syncKeyboardAppearancePreviewPid()
})

watch(
  deviceList,
  () => {
    syncKeyboardAppearancePreviewPid()
  },
  { immediate: true },
)

const min = () => electronAPI.min()
const max = async () => {
  const maximized = Boolean(await electronAPI.wnd('isMaximized'))
  await electronAPI.wnd(maximized ? 'unmaximize' : 'maximize')
  isWindowMaximized.value = !maximized
}
const out = () => electronAPI.close()
</script>

<style lang="less" scoped>
.select_device {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding-top: 72px;

  .selector_top_actions {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 30;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    max-width: calc(100vw - 40px);
    -webkit-app-region: no-drag;

    .appearance_action {
      padding: 10px 18px;
      font-size: 14px;
      font-weight: 700;
      white-space: nowrap;
    }

    .selector_window_controls {
      display: flex;
      align-items: center;
      gap: 4px;

      .window-btn {
        width: 30px;
        height: 30px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        border-radius: var(--radius-m);
        background: transparent;
        color: var(--sidebar-text-muted);
        cursor: pointer;
        transition: var(--transition-base);

        svg,
        .svg_asset {
          width: 14px;
          height: 14px;
          stroke: currentColor;
          stroke-width: 2;
        }

        &:hover {
          transform: translateY(-2px);
          background: rgba(0, 0, 0, 0.06);
          color: var(--shell-text-primary);
        }

        &.close-btn:hover {
          background: rgba(255, 87, 87, 0.16);
          color: #d33b2f;
        }
      }
    }
  }

  .select_device_scroll {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
    min-height: 0;
    max-height: 600px;
    gap: 80px;
    padding: 60px 0; /* 增加上下内边距，确保阴影不被剪裁 */
    scroll-snap-type: x mandatory;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .select_device_container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 700px;
    flex-shrink: 0;
    padding: 20px;
    cursor: pointer;
    scroll-snap-align: center;
    height: 480px; /* 适当增加高度 */

    .card_inner {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: var(--radius-l);
      padding: 30px;
      transition: var(--transition-base);
      box-shadow: var(--shadow-md);
      overflow: visible; /* 允许投影溢出容器而不被剪裁 */

      &:hover {
        background: rgba(255, 255, 255, 0.15);
        transform: translateY(-10px);
        box-shadow: var(--shadow-lg);
      }
    }

    &.device_select .card_inner {
      border-color: #fff;
      background: rgba(255, 255, 255, 0.2);
      box-shadow: 0 0 30px rgba(255, 255, 255, 0.1);
    }

    // 移除之前的强制 margin 居中，改用透明占位符或 padding
    &:first-child {
      margin-left: calc(50% - 350px) !important;
    }

    &:last-child {
      margin-right: calc(50% - 350px) !important;
    }

    .name {
      font-size: 32px;
      font-weight: 600;
      color: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      .connect_mode {
        width: 36px;
        height: 36px;
        background-color: rgba(#ffffff, 0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-m);
        margin-left: 16px;

        .m_24g {
          height: 20px;
        }

        .m_24g8k {
          height: 24px;
        }
      }
    }

    .device_img {
      margin-top: 20px;
      width: 460px;
      height: 260px;
      object-fit: contain;
      filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.2));
    }
  }

  .page_container {
    display: flex;
    margin-top: 10px; /* 进一步压缩间距，为大卡片阴影留空间 */

    .page {
      width: 40px;
      height: 6px;
      border-radius: 3px;
      background-color: rgba(#ffffff, 0.3);
      cursor: pointer;
      transition: var(--transition-base);
      margin: 0 6px;

      &.action {
        width: 60px;
        background-color: #ffffff !important;
      }
    }
  }

  .device_notice {
    margin-top: 10px;
    color: rgba(#ffffff, 0.7);
    font-size: 14px;
    height: 30px;
  }
}

.btn_container {
  display: flex;
  margin-top: 25px;
  gap: 20px;
}

.appearance_action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  .action_icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    color: currentColor;

    :deep(svg),
    :deep(.svg_asset) {
      width: 18px;
      height: 18px;
      stroke: currentColor;
      stroke-width: 2.5;
    }
  }
}

.request {
  padding: 12px 32px;
  font-size: 15px;
  font-weight: 600;
}

@media (max-width: 900px) {
  .select_device {
    padding-top: 60px;

    .selector_top_actions {
      top: 12px;
      right: 12px;
      max-width: calc(100vw - 24px);

      .appearance_action {
        padding: 9px 14px;
        font-size: 13px;
      }
    }
  }
}
</style>
